/**
 * Miniature previews for OCEN's registered section types, shown as the tiles
 * of @kevlarcon/pages' section-type strip (`SectionTypeStrip`, wired up via
 * `PagesConfig.previewComponents` — see ../site/SitePagesProvider.tsx).
 * The shared `section_types_library` types (Hero family, FeatureIntro, Stats,
 * Paths, Testimonial, CtaStrip) reuse the library's own
 * `createLibrarySectionPreviews` glyph compositions, recolored to this
 * site's gold/navy editor theme — only sgokit-local types get bespoke
 * glyphs here. Keyed to match `tenant_site/section_types.py` exactly.
 *
 * Each glyph is a floor plan of the real section, not an icon: things sit on
 * the side of the tile they sit on in the page, so the strip reads as a
 * thumbnail of what you'd get.
 */
import {
  createLibrarySectionPreviews, CtaPillGroupGlyph, FormFieldsGlyph, GlyphPalette, TextBlockGlyph, UsMapGlyph,
} from '@kevlarcon/pages';

const VIEWBOX = '0 0 120 80';
const GOLD = '#ddab2b';
const NAVY = '#0d1c31';
const NAVY_FAINT = '#c7ccd4';
const FAINT = '#eef0f3';

const PALETTE: GlyphPalette = { ink: NAVY, accent: GOLD, muted: NAVY_FAINT, faint: FAINT };

const Svg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox={VIEWBOX} width="100%" height="100%">{children}</svg>
);

/** Eyebrow, heading and lede centered over the section, as every map
 * section opens. */
const CenteredHead: React.FC = () => (
  <>
    <rect x={53} y={6} width={14} height={2.5} rx={1} fill={GOLD} />
    <rect x={28} y={11} width={64} height={6} rx={2} fill={NAVY} />
    <rect x={36} y={20} width={48} height={2.5} rx={1} fill={NAVY_FAINT} />
  </>
);

// A warm full-bleed band: copy and two buttons left; right, a card holding
// the credit amount over a grid of state chips.
export const TaxCreditPreview = () => (
  <Svg>
    <rect x={0} y={0} width={120} height={80} fill={GOLD} opacity={0.16} />
    <TextBlockGlyph palette={PALETTE} x={8} y={16} width={46} headingLines={2} lines={2} compact />
    <CtaPillGroupGlyph palette={PALETTE} x={8} y={57} count={2} pillWidth={20} pillHeight={7} allFilled />
    <rect x={64} y={10} width={48} height={60} rx={2} fill="#fff" stroke={NAVY_FAINT} strokeWidth={1} />
    <rect x={69} y={15} width={16} height={2} rx={1} fill={NAVY_FAINT} />
    <rect x={69} y={21} width={28} height={9} rx={2} fill={NAVY} />
    <rect x={69} y={35} width={38} height={1} fill={NAVY_FAINT} />
    {[0, 1, 2].map((row) => [0, 1, 2, 3, 4].map((col) => (
      <rect
        key={`${row}-${col}`} x={69 + col * 7.6} y={41 + row * 7} width={6} height={4.5} rx={1}
        fill="none" stroke={NAVY_FAINT} strokeWidth={1}
      />
    )))}
  </Svg>
);

// Centered head over the national map, with its legend to the right.
export const StatesPreview = () => (
  <Svg>
    <CenteredHead />
    <UsMapGlyph palette={PALETTE} x={8} y={30} w={76} h={44} />
    <rect x={90} y={40} width={1.2} height={20} fill={GOLD} />
    <rect x={94} y={41} width={18} height={3} rx={1} fill={NAVY_FAINT} />
    <rect x={94} y={48} width={16} height={2} rx={1} fill={NAVY_FAINT} />
    <rect x={94} y={52} width={12} height={2} rx={1} fill={NAVY_FAINT} />
  </Svg>
);

// One state's own map, framed on itself among its neighbors.
export const StateTemplatePagePreview = () => (
  <Svg>
    <CenteredHead />
    {[
      'M14 32 L44 30 L42 52 L12 54 Z', 'M80 30 L108 32 L110 52 L82 54 Z',
      'M16 58 L44 56 L46 76 L18 76 Z', 'M80 56 L106 58 L104 76 L82 76 Z',
    ].map((d) => <path key={d} d={d} fill={FAINT} stroke={NAVY_FAINT} strokeWidth={1} />)}
    <path d="M46 31 L78 29 L82 50 L76 75 L48 75 L43 54 Z" fill={NAVY} />
  </Svg>
);

const PinnedMap: React.FC<{ y: number }> = ({ y }) => (
  <>
    <rect x={36} y={y} width={76} height={72 - y + 2} rx={2} fill={FAINT} />
    <path d={`M46 ${y + 44} L54 ${y + 8} L96 ${y + 10} L102 ${y + 42} Z`} fill="none" stroke={NAVY_FAINT} strokeWidth={1.5} />
    {[[62, 20], [80, 30], [88, 14]].map(([cx, dy]) => (
      <circle key={cx} cx={cx} cy={y + dy} r={3} fill={GOLD} />
    ))}
  </>
);

const ResultList: React.FC<{ from: number }> = ({ from }) => (
  <>
    {[0, 1, 2, 3].map((i) => (
      <g key={i}>
        <rect x={8} y={from + i * 13} width={22} height={3} rx={1} fill={NAVY} />
        <rect x={8} y={from + i * 13 + 5} width={16} height={2} rx={1} fill={NAVY_FAINT} />
      </g>
    ))}
  </>
);

// Results list (left quarter) beside a map with pins (right three quarters).
export const PartnerMapPreview = () => (
  <Svg>
    <rect x={8} y={8} width={44} height={5} rx={2} fill={NAVY} />
    <ResultList from={20} />
    <PinnedMap y={18} />
  </Svg>
);

// The same list-and-map, under a search bar.
export const PartnerDirectoryPreview = () => (
  <Svg>
    <rect x={8} y={6} width={104} height={8} rx={4} fill="#fff" stroke={NAVY_FAINT} strokeWidth={1.2} />
    <circle cx={14} cy={10} r={2} fill="none" stroke={NAVY} strokeWidth={1.2} />
    <rect x={20} y={9} width={28} height={2} rx={1} fill={NAVY_FAINT} />
    <ResultList from={24} />
    <PinnedMap y={22} />
  </Svg>
);

// A CTA strip: heading and lede left, the fixed join / sign-in buttons right.
export const PartnerEntryPreview = () => (
  <Svg>
    <rect x={0} y={20} width={120} height={40} fill={FAINT} />
    <rect x={10} y={32} width={46} height={7} rx={2} fill={NAVY} />
    <rect x={10} y={43} width={40} height={2.5} rx={1} fill={NAVY_FAINT} />
    <CtaPillGroupGlyph palette={PALETTE} x={66} y={36} count={2} pillWidth={20} pillHeight={8} />
  </Svg>
);

// A long two-column form in titled groups.
export const PartnerJoinFormPreview = () => (
  <Svg>
    {[[6, [12, 22]], [36, [42, 52]], [64, [70]]].map(([gy, rows]) => (
      <g key={gy as number}>
        <rect x={8} y={gy as number} width={22} height={3} rx={1} fill={NAVY} />
        {(rows as number[]).map((ry) => [8, 62].map((rx) => (
          <g key={`${ry}-${rx}`}>
            <rect x={rx} y={ry} width={12} height={2} rx={1} fill={NAVY_FAINT} />
            <rect x={rx} y={ry + 3.5} width={50} height={5} rx={1} fill="#fff" stroke={NAVY_FAINT} strokeWidth={1} />
          </g>
        )))}
      </g>
    ))}
  </Svg>
);

// A narrow centered card: a head over a short form. `mark` tells the donate
// form ($) from the apply gate (lock).
const CenteredFormCard: React.FC<{ mark: 'dollar' | 'lock' }> = ({ mark }) => (
  <Svg>
    <rect x={28} y={6} width={64} height={68} rx={3} fill="#fff" stroke={NAVY_FAINT} strokeWidth={1.2} />
    <rect x={53} y={12} width={14} height={2.5} rx={1} fill={GOLD} />
    <rect x={42} y={17} width={36} height={5} rx={2} fill={NAVY} />
    <g transform="translate(26 22) scale(0.6)">
      <FormFieldsGlyph palette={PALETTE} mark={mark} />
    </g>
  </Svg>
);
export const DonateFormPreview = () => <CenteredFormCard mark="dollar" />;
export const ApplyGatePreview = () => <CenteredFormCard mark="lock" />;

export const homeSectionPreviews = {
  ...createLibrarySectionPreviews(PALETTE),
  TaxCredit: TaxCreditPreview,
  States: StatesPreview,
  StateTemplatePage: StateTemplatePagePreview,
  PartnerMap: PartnerMapPreview,
  PartnerDirectory: PartnerDirectoryPreview,
  PartnerEntry: PartnerEntryPreview,
  PartnerJoinForm: PartnerJoinFormPreview,
  DonateForm: DonateFormPreview,
  ApplyGate: ApplyGatePreview,
};
